import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { useState } from 'react';
import type { AppError } from '@/api/client';
import { useVenueContext } from '@/providers/VenueProvider';

type LoginForm = { email: string; password: string };

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSlug } = useVenueContext();
  const [error, setError] = useState<string | null>(null);
  const from = (location.state as { from?: string })?.from;

  const loginSchema = z.object({
    email: z.string().email(t('validation.email_invalid')),
    password: z.string().min(1, t('validation.required')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    try {
      const user = await login(data.email, data.password);
      if (from) {
        navigate(from, { replace: true });
      } else {
        const isOperator = user.roles.some(r => r === 'OPERATOR_ADMIN' || r === 'OPERATOR_STAFF' || r === 'PLATFORM_ADMIN');
        navigate(isOperator ? '/admin' : `/venue/${currentSlug}/book`, { replace: true });
      }
    } catch (err) {
      const appError = err as AppError;
      if (appError.code === 'INVALID_CREDENTIALS') {
        setError(t('auth.error_invalid_credentials'));
      } else if (appError.code === 'ACCOUNT_LOCKED') {
        setError(t('auth.error_account_locked'));
      } else {
        setError(t('auth.error_generic'));
      }
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4 pt-6">
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
            />
            {errors.password && (
              <p className="text-xs text-destructive">{errors.password.message}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('common.loading') : t('auth.login')}
          </Button>
        </CardContent>
        <CardFooter className="flex flex-col gap-2 text-sm text-muted-foreground">
          <Link to="/forgot-password" className="hover:text-primary">
            {t('auth.forgot_password')}
          </Link>
          <div>
            {t('auth.no_account')}{' '}
            <Link to="/register" state={{ from }} className="font-medium text-primary hover:underline">
              {t('auth.register')}
            </Link>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
